import Logo from '@/assets/logo.svg';
import { Icon } from '@/components/ui/Icon';
import { usePermissions } from '@/hooks/usePermissions';
import { Action, Subject } from '@/lib/permissions';
import { useUI } from '@/lib/uiContext';
import {
  mdiAccount,
  mdiBell,
  mdiCellphoneArrowDown,
  mdiChartBar,
  mdiClose,
  mdiFileChartOutline,
  mdiFilter,
  mdiImport,
  mdiMonitor,
  mdiStore,
  mdiTag,
} from '@mdi/js';
import cx from 'classnames';
import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';

interface MenuItem {
  label: string;
  href: string;
  icon: string;
  requiredPermission?: { action: Action; subject: Subject };
}

const menuItems: MenuItem[] = [
  {
    label: 'Dashboard',
    href: '/dashboard',
    icon: mdiMonitor,
  },
  {
    label: 'Analytics',
    href: '/analytics',
    icon: mdiChartBar,
    requiredPermission: { action: 'read', subject: 'Analytics' },
  },
  {
    label: 'Event Report',
    href: '/event-report',
    icon: mdiFileChartOutline,
    requiredPermission: { action: 'read', subject: 'UserEvent' },
  },
  {
    label: 'Users',
    href: '/users',
    icon: mdiAccount,
    requiredPermission: { action: 'read', subject: 'User' },
  },
  {
    label: 'Businesses',
    href: '/businesses',
    icon: mdiStore,
    requiredPermission: { action: 'read', subject: 'Business' },
  },
  {
    label: 'Import',
    href: '/import',
    icon: mdiImport,
    requiredPermission: { action: 'create', subject: 'Business' },
  },
  {
    label: 'Categories',
    href: '/categories',
    icon: mdiTag,
    requiredPermission: { action: 'read', subject: 'Category' },
  },
  {
    label: 'Filters',
    href: '/filters',
    icon: mdiFilter,
    requiredPermission: { action: 'read', subject: 'Filter' },
  },
  {
    label: 'Notifications',
    href: '/notifications',
    icon: mdiBell,
    requiredPermission: { action: 'read', subject: 'Notification' },
  },
  {
    label: 'App Versions',
    href: '/app-versions',
    icon: mdiCellphoneArrowDown,
    requiredPermission: { action: 'read', subject: 'AppVersion' },
  },
];

export const Sidebar: React.FC = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const { sidebarOpen, toggleSidebar } = useUI();
  const { can } = usePermissions();

  const handleClose = () => {
    toggleSidebar();
  };

  const isActive = (href: string) => {
    return (
      pathname === href || (href !== '/dashboard' && pathname.startsWith(href))
    );
  };

  const canAccess = (item: MenuItem) => {
    if (!item.requiredPermission) return true;
    return can(item.requiredPermission.action, item.requiredPermission.subject);
  };

  return (
    <>
      {/* Sidebar */}
      <aside
        className={cx(
          'w-64 h-screen bg-white border-r border-grey-200 fixed top-0 left-0 z-10 transform transition-transform duration-300 ease-in-out lg:transform-none',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Header */}
        <div className='flex items-center justify-between p-6 border-b border-grey-200'>
          <div className='flex items-center space-x-2'>
            <img src={Logo} alt='Coffice Admin' className='h-8' />
          </div>
          <button
            onClick={handleClose}
            className='lg:hidden inline-flex items-center justify-center p-2 text-grey-500 hover:bg-grey-100 hover:text-grey-700 rounded-sm transition-colors'
          >
            <Icon path={mdiClose} size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className='p-4'>
          <ul className='space-y-1'>
            {menuItems.filter(canAccess).map(item => (
              <li key={item.href}>
                <Link
                  to={item.href}
                  className={cx(
                    'relative flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                    isActive(item.href)
                      ? 'bg-grey-100 text-grey-900'
                      : 'text-grey-600 hover:bg-grey-50 hover:text-grey-900',
                  )}
                  onClick={() => {
                    // Close sidebar on mobile when clicking a link
                    if (window.innerWidth < 1024) {
                      handleClose();
                    }
                  }}
                >
                  {/* Orange active indicator bar */}
                  {isActive(item.href) && (
                    <span className='absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-coffice-orange rounded-r-full' />
                  )}
                  <Icon
                    path={item.icon}
                    size={18}
                    className={cx(
                      'mr-2 transition-opacity',
                      isActive(item.href) ? 'opacity-100' : 'opacity-70',
                    )}
                  />
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
    </>
  );
};
